/**
 * The songs of a practice as this user gets them (its practice list, shared/program.ts): numbered in
 * program order, the songs of a folder / setlist under its name. Each song links into the practice
 * (practiceSongHref: its list tab, ‹ Fyrra · 3 / 12 · Næsta ›). Folders / setlists with no song this
 * user can see are left out, like songs they cannot see.
 */
import { ListMusic } from 'lucide-react';
import { Link } from 'react-router';
import type { CollectionDTO, SongListItemDTO } from '../../shared/dto/library';
import type { PracticeListDTO, ProgramGroup } from '../../shared/program';
import { cx } from '../components/ui';
import { useT } from '../i18n';
import { creditsLine } from '../screens/library/format';
import { orderedEntries } from '../viewer/setlist';
import { practiceSongHref } from './model';

const hasGroups = (list: CollectionDTO): list is PracticeListDTO => Array.isArray((list as Partial<PracticeListDTO>).groups);

export function ProgramList({
  eventId,
  list,
  songs,
  currentEntryId,
  limit,
  onOpen,
}: {
  eventId: string;
  list: CollectionDTO;
  songs: ReadonlyMap<string, SongListItemDTO>;
  /** The entry open on the song page (marked). */
  currentEntryId?: string | null;
  /** Show only the first `limit` songs. */
  limit?: number;
  /** Called when a song is opened (e.g. to close the sheet it is in). */
  onOpen?: () => void;
}) {
  const t = useT();
  const ordered = orderedEntries(list.entries);
  const groups: ProgramGroup[] = hasGroups(list)
    ? list.groups
    : [{ itemId: list.id, type: 'song', refId: '', name: null, first: 0, count: ordered.length }];
  const shown = limit ?? ordered.length;

  return (
    <div className="flex flex-col gap-1">
      {groups.map((g) => {
        if (g.count === 0 || g.first >= shown) return null;
        const rows = ordered.slice(g.first, Math.min(g.first + g.count, shown));
        return (
          <div key={g.itemId} className="flex flex-col gap-1">
            {g.type === 'collection' ? (
              <h3 className="mt-2 flex items-center gap-2 px-1 text-[14px] font-semibold tracking-wide text-[var(--muted)] uppercase">
                <ListMusic className="size-4 shrink-0" aria-hidden />
                <span className="truncate">{g.name ?? t('practice.unknown_list')}</span>
                <span className="shrink-0 font-normal normal-case">· {t('practice.count', { count: g.count })}</span>
              </h3>
            ) : null}
            <ul className="flex flex-col gap-1">
              {rows.map((e, k) => {
                const index = g.first + k;
                const song = songs.get(e.songId);
                const current = !!currentEntryId && currentEntryId === e.entryId;
                return (
                  <li key={e.entryId}>
                    <Link
                      to={practiceSongHref(eventId, index, e)}
                      onClick={onOpen}
                      aria-current={current ? 'true' : undefined}
                      className={cx(
                        'flex min-h-14 items-center gap-3 rounded-2xl px-3 py-2 transition hover:bg-[var(--accent-soft)]',
                        current && 'bg-[var(--accent-soft)] ring-2 ring-[var(--accent)]',
                      )}
                    >
                      <span className="flex size-9 shrink-0 items-center justify-center rounded-full bg-[var(--surface-2)] text-[15px] font-bold tabular-nums">
                        {index + 1}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-[17px] font-semibold">{song?.title ?? t('practice.unknown_song')}</span>
                        {song && (song.composer || song.arranger) ? (
                          <span className="block truncate text-[14px] text-[var(--muted)]">{creditsLine(t, song)}</span>
                        ) : null}
                        {e.note ? <span className="block truncate text-[14px] text-[var(--muted)] italic">{e.note}</span> : null}
                      </span>
                      {current ? <span className="shrink-0 text-[13px] font-bold text-[var(--accent)]">{t('practice.current')}</span> : null}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        );
      })}
    </div>
  );
}
